import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import { TimelineSkeleton, withSuspense } from "@opensea/ui-kit"
import { ActionTimeline } from "../../../../../components/ActionTimeline"
import { ActionTimelineFragment } from "../../../../../components/ActionTimeline/ActionTimeline.fragment"
import { useAddress } from "../../../../../polychain/hooks"
import type { TransferFlowFormValues } from "../../types"
import { TransferFlowTimeline_itemFragment } from "./TransferFlowTimeline.fragment"

const TransferFlowTimelineQuery = graphql(
  `
    query TransferFlowTimelineQuery(
      $assetQuantities: [AssetQuantityInput!]!
      $fromAddress: Address!
      $toAddress: Address!
    ) {
      transferAssetsV2(
        fromAddress: $fromAddress
        toAddress: $toAddress
        assetQuantities: $assetQuantities
      ) {
        actions {
          ...ActionTimeline
        }
      }
    }
  `,
  [ActionTimelineFragment],
)

type Props = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  items: FragmentOf<typeof TransferFlowTimeline_itemFragment>[]
  formValues: TransferFlowFormValues
  onBack?: () => void
}

export const TransferFlowTimeline = withSuspense(
  ({ onSuccess, items: itemKeys, formValues, onBack }: Props) => {
    const items = itemKeys.map(itemKey =>
      readFragment(TransferFlowTimeline_itemFragment, itemKey),
    )

    const wallet = useAddress()

    const [{ data }] = useQuery({
      query: TransferFlowTimelineQuery,
      variables: {
        fromAddress: wallet as NonNullable<string>,
        assetQuantities: items.map(item => ({
          asset: {
            tokenId: item.tokenId,
            contractAddress: item.contractAddress,
            chain: item.chain.identifier,
          },
          quantity: (
            formValues.items.find(field => field.id === item.id)?.quantity ?? 1
          ).toString(),
        })),
        toAddress:
          formValues.recipient.resolvedAddress ??
          formValues.recipient.identifier ??
          "",
      },
      pause: !wallet,
    })

    if (!data) {
      return <TimelineSkeleton />
    }

    return (
      <ActionTimeline
        actions={data.transferAssetsV2.actions}
        onAllActionsCompleted={onSuccess}
        onBack={onBack}
      />
    )
  },
  <TimelineSkeleton />,
)
